import React, { FC } from 'react'
import { INavListFooterIndicatorProps } from 'transfix-ui/components/NavListFooterIndicator'
import SideNav from 'transfix-ui/components/SideNav/SideNav'
import { Drawer, createStyles, makeStyles, DrawerClassKey } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { INavRouteGroup } from 'types'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useNavContext } from 'utils/context/NavContext'

const DRAWER_WIDTH = 65
const DRAWER_WIDTH_EXPANDED = 267

interface IUseSideNavDrawerStylesProps {
  /**
   * @default true
   */
  isNavExpanded?: boolean
}

const useSideNavDrawerStyles = makeStyles(theme =>
  createStyles<Extract<DrawerClassKey, 'root' | 'paper'>, IUseSideNavDrawerStylesProps>({
    root: {
      /**
       * Overrides some legacy styles from .side-nav.expanded selector
       */
      flex: '0 0 auto !important',
      width: ({ isNavExpanded = true }) => (isNavExpanded ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH),
      transition: `all 150ms ${theme.transitions.easing.sharp}`,
    },
    paper: {
      width: ({ isNavExpanded = true }) => (isNavExpanded ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH),
      transition: `all 150ms ${theme.transitions.easing.sharp}`,
      borderRight: `1px solid ${brandColors.coolGray7}`,
    },
  })
)

interface ISideNavDrawerProps {
  /**
   * @example '/home'
   */
  homeRoute?: string
  /**
   * Route configuration array
   * @default []
   */
  navRouteGroups: INavRouteGroup[]
  /**
   * @example <FMSHeaderLogo logoStyle='black' />
   */
  logo?: React.ReactElement
  navFooterIndicator?: INavListFooterIndicatorProps
}

/**
 * Handles logic for showing/hiding SideNav, and renders different
 * versions for mobile and desktop
 */
export const SideNavDrawer: FC<ISideNavDrawerProps> = ({
  navRouteGroups = [],
  logo: logoProp,
  homeRoute = '',
  navFooterIndicator,
}) => {
  const { isNavExpanded, isNavOpen: isMobileOpen, toggleExpand, toggleNav } = useNavContext()
  const { isMobile } = useBreakpointContext()
  const classes = useSideNavDrawerStyles({ isNavExpanded })

  return (
    <>
      {isMobile ? (
        <Drawer
          variant='temporary'
          anchor='left'
          open={isMobileOpen}
          classes={classes}
          onClose={toggleNav}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}>
          {/**
           * Mobile
           */}
          <SideNav
            isNavExpanded
            homeRoute={homeRoute}
            onToggleNav={toggleNav}
            navRouteGroups={navRouteGroups}
            onClick={toggleNav}
            logo={logoProp}
            navFooterIndicator={{ ...navFooterIndicator, isNavExpanded }}
          />
        </Drawer>
      ) : (
        <Drawer classes={classes} variant='permanent' open>
          {/**
           * Desktop
           */}
          <SideNav
            homeRoute={homeRoute}
            isNavExpanded={isNavExpanded}
            onToggleNav={toggleExpand}
            navRouteGroups={navRouteGroups}
            logo={logoProp}
            navFooterIndicator={{ ...navFooterIndicator, isNavExpanded }}
          />
        </Drawer>
      )}
    </>
  )
}

export default SideNavDrawer
